/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { useCosmosComponentContext } from "@components/context";
import { OptimizedCosmosAssetFragment } from "@components/elements";
import { getHygraphImageIds, isDirectoryAssetSrc, isHygraphImageSrc } from "@components/utils";

/**
 * Satisfies the next/image loader.  Adds the ?width param to satisfy the check for optimized.
 * The is the default loader.
 */

const hygraphImageLoader = (hygraphMediaDomain: string) => {
	return ({ src, width }: { src?: string; width?: number }): string => {
		if (!src) return "";

		// Directory-data images are pre-sized static assets served under `<host>/directory/`.
		// Their `directory` path segment collides with the Hygraph environment-id slot, so the
		// transform logic below would splice a bogus `resize=…/quality=…/auto_image` path the CDN
		// won't serve. Per the directory CDN contract these files are already sized at bake time
		// by the s3-sync producer, so transformation is intentionally skipped here: serve them
		// untransformed and only append `?width` so next/image still treats the result as
		// optimized (mirrors the cdn2 branch below). Tradeoff: because no per-width resize is
		// applied, sizing is the producer's responsibility — an oversized source ships at full
		// resolution. Enforce a max dimension in the producer, not in this synchronous loader
		// (it has neither the byte size nor the intrinsic dimensions to gate on).
		if (isDirectoryAssetSrc(src)) {
			if (!width) return src;
			try {
				const url = new URL(src);
				url.searchParams.set("width", `${width}`);
				return url.toString();
			} catch {
				return src;
			}
		}

		//check for hygraph assets
		if (isHygraphImageSrc(hygraphMediaDomain, src)) {
			// Handle blur-processed URLs from /process endpoint
			if (src.includes("/process?") && src.includes("blur=")) {
				// Just append width param for next-image optimization check
				try {
					const url = new URL(src);
					if (!url.searchParams.has("width")) {
						url.searchParams.set("width", `${width || 0}`);
					}
					return url.toString();
				} catch {
					return src;
				}
			}

			const hasTransformations =
				(src.includes("output=format") || src.includes("/auto_image")) &&
				(src.includes("resize=width") || src.includes("quality=value"));

			if (hasTransformations) {
				// URL already carries transformations baked in at SSR time, but next/image
				// still calls the loader per srcSet entry with a different `width`. Rewrite
				// the resize segment (or insert one) so we emit a real per-width variant
				// instead of returning the same baked URL for every srcSet entry.
				try {
					const url = new URL(src);
					let path = url.pathname;

					if (width) {
						if (/\/resize=width:\d+\//.test(path)) {
							path = path.replace(/\/resize=width:\d+\//, `/resize=width:${width}/`);
						} else {
							// insert after the environment segment (first path part after leading slash)
							path = path.replace(/^\/([^/]+)\//, `/$1/resize=width:${width}/`);
						}
						url.pathname = path;
						url.searchParams.set("width", `${width}`);
						return url.toString();
					}

					// no width argument — just ensure ?width= is present so next/image is happy
					if (!url.searchParams.has("width")) {
						const matches = path.match(/\/resize=width:(\d+)\//);
						url.searchParams.set("width", matches?.[1] ?? "0");
					}
					return url.toString();
				} catch {
					return src;
				}
			}
		} else {
			//try configure URL for Akamai Image Manager on cdn2
			if (width) {
				try {
					const url = new URL(src);

					//make adjustments for CMS cdn2
					if (url.hostname === "cdn2.unrealengine.com") {
						//use the w param instead of the width
						const paramWidth = url.searchParams.get("w");

						//force akamai url params to optimze images on cdn2
						url.searchParams.set("resize", "1");
						url.searchParams.set("w", `${paramWidth || width}`);
						url.searchParams.set("width", `${paramWidth || width}`);
					} else {
						url.searchParams.set("width", `${width}`);
					}
					return url.toString();
				} catch {
					console.warn("Could not parse image src to append width");
				}
			}
			return src;
		}

		const { assetId, environmentId } = getHygraphImageIds(hygraphMediaDomain, src);
		if (!assetId) return "";

		const envPartStr = environmentId ? `/${environmentId}` : "";
		// Preserve the origin domain from the source URL rather than forcing the env-configured
		// domain — feed API responses always use the prod CDN, even in local/staging envs.
		let host = hygraphMediaDomain;
		try {
			host = new URL(src).hostname || hygraphMediaDomain;
		} catch {
			// fall through to configured domain
		}

		if (!width) {
			//the width url param makes next happy
			return `https://${host}/${assetId}/quality=value:90/auto_image${envPartStr}?width=${0}`;
		}

		return `https://${host}/${assetId}/resize=width:${width}/quality=value:90/auto_image${envPartStr}?width=${width}`;
	};
};

/**
 * Does not run the image transformation and still satisfies the check for optimized.
 * This should be used for svg files since they can't be transformed.
 */
const disabledHygraphImageLoader = (hygraphMediaDomain: string) => {
	return ({ src, width }: { src?: string; width?: number }): string => {
		if (!src) return "";

		//Only try for hygraph assets
		if (!isHygraphImageSrc(hygraphMediaDomain, src)) return src;

		const { assetId, environmentId } = getHygraphImageIds(hygraphMediaDomain, src);
		if (!assetId) return "";

		const envPartStr = environmentId ? `/${environmentId}` : "";

		return `https://${hygraphMediaDomain}/${assetId}${envPartStr}?width=${width}`;
	};
};

export const useHygraphImageLoader = (): ReturnType<typeof hygraphImageLoader> => {
	const { hygraphMediaDomain } = useCosmosComponentContext();
	return hygraphImageLoader(hygraphMediaDomain);
};

export const useAssetOptUrl = (asset: OptimizedCosmosAssetFragment | null | undefined): string => {
	const imageLoader = useHygraphImageLoader();
	if (!asset) return "";
	if (asset.optUrl) return asset.optUrl;

	const optUrl = imageLoader({ src: asset.url, width: asset.width || undefined });
	return optUrl;
};

export const useDisabledHygraphImageLoader = (): ReturnType<typeof disabledHygraphImageLoader> => {
	const { hygraphMediaDomain } = useCosmosComponentContext();
	return disabledHygraphImageLoader(hygraphMediaDomain);
};
